import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AddSuppliersContract.css";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

registerLocale("ro", ro);

const AddSupplier = () => {
  const navigate = useNavigate();
  const animatedComponents = makeAnimated();

  const [suppliers, setSuppliers] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [contractDate, setContractDate] = useState(null);
  const [expireDate, setExpireDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    contract_date: "",
    expire_date: "",
    provider_contract_nr: "",
    contract_description: "",
    alert_days: 14,
    status: "New",
    supplier: "",
    remarks: "",
  });

  const handleDateChange = (date, fieldName) => {
    let formattedDate = "";

    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      formattedDate = `${year}-${month}-${day}`;
    }

    setFormData((prev) => ({
      ...prev,
      [fieldName]: formattedDate,
    }));

    if (fieldName === "contract_date") {
      setContractDate(date);
    } else if (fieldName === "expire_date") {
      setExpireDate(date);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "alert_days" ? Number(value) : value,
    }));
  };

  const handleSupplierChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      supplier: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleStatusChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      status: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.contract_date || !formData.expire_date) {
      alert("Te rog completează datele contractului.");
      return;
    }

    if (!formData.supplier) {
      alert("Te rog selectează supplierul.");
      return;
    }

    const payload = {
      contract_date: formData.contract_date,
      expire_date: formData.expire_date,
      provider_contract_nr: formData.provider_contract_nr,
      contract_description: formData.contract_description,
      alert_days: Number(formData.alert_days),
      status: formData.status,
      supplier: formData.supplier,
      remarks: formData.remarks,
    };

    try {
      setLoading(true);

      const response = await axiosInstance.post("/supplier-contracts/", payload);

      console.log("Supplier contract added successfully:", response.data);
      navigate(-1);
    } catch (error) {
      console.error("Error adding supplier contract:", error.response?.data || error.message);
      alert(
        typeof error.response?.data === "string"
          ? error.response.data
          : JSON.stringify(error.response?.data || { detail: error.message }, null, 2)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSuppliersForUser = async () => {
      try {
        const departmentResponse = await axiosInstance.get("/department/user/");
        const accessibleDepartments = departmentResponse.data.map((dep) => dep.value);

        const statusSupplierDeptResponse = await axiosInstance.get("/status-supplier-department/");
        const supplierDepartments = statusSupplierDeptResponse.data;

        const allowedSupplierIds = supplierDepartments
          .filter(
            (item) =>
              item.status === "Active" &&
              accessibleDepartments.includes(item.department)
          )
          .map((item) => item.supplier);

        const suppliersResponse = await axiosInstance.get("/suppliers/");
        const accessibleSuppliers = suppliersResponse.data.filter((supplier) =>
          allowedSupplierIds.includes(supplier.id)
        );

        const supplierOptions = accessibleSuppliers.map((supplier) => ({
          value: supplier.id,
          label: supplier.name,
        }));

        setSuppliers(supplierOptions);
      } catch (error) {
        console.error("Eroare la preluarea supplierilor:", error);
      }
    };

    fetchSuppliersForUser();
  }, []);

  useEffect(() => {
    axiosInstance
      .get("/status-choices-supplier/")
      .then((response) => {
        const options = response.data.status_choices.map(([value, label]) => ({
          value,
          label,
        }));
        setStatusOptions(options);
      })
      .catch((error) => console.error("Error fetching status choices:", error));
  }, []);

  return (
    <div className="add-supplier-contract-page">
      <div className="add-supplier-container">
        <div className="supplier-header">
          <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
          <h2 className="add-supplier-title">Add Suppliers Contracts</h2>
        </div>
      </div>

      <hr className="title-separator" />

      <div className="add-supplier-container">
        <form onSubmit={handleSubmit} className="add-supplier-form">
          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="contract_date" className="label">
                Contract Date <span style={{ color: "red" }}>*</span>
              </label>
              <div className="datepicker-container addsuppliers-datepicker">
                <DatePicker
                  selected={contractDate}
                  onChange={(date) => handleDateChange(date, "contract_date")}
                  dateFormat="dd.MM.yyyy"
                  locale="ro"
                  placeholderText="dd.mm.yyyy"
                  className="datepicker-input"
                  required
                  wrapperClassName="datepicker-wrapper"
                />
                <span className="calendar-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="expire_date" className="label">
                Finish Contract Date <span style={{ color: "red" }}>*</span>
              </label>
              <div className="datepicker-container addsuppliers-datepicker">
                <DatePicker
                  selected={expireDate}
                  onChange={(date) => handleDateChange(date, "expire_date")}
                  dateFormat="dd.MM.yyyy"
                  locale="ro"
                  placeholderText="dd.mm.yyyy"
                  className="datepicker-input"
                  required
                  wrapperClassName="datepicker-wrapper"
                />
                <span className="calendar-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="provider_contract_nr" className="label">
              Provider Contract - Number & Date <span style={{ color: "red" }}>*</span>
            </label>
            <input
              id="provider_contract_nr"
              type="text"
              name="provider_contract_nr"
              value={formData.provider_contract_nr}
              onChange={handleChange}
              className="input"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="contract_description" className="label">
              Contract Description <span style={{ color: "red" }}>*</span>
            </label>
            <textarea
              id="contract_description"
              name="contract_description"
              value={formData.contract_description}
              onChange={handleChange}
              className="textarea"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="supplier" className="label">
              Supplier <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              id="supplier"
              name="supplier"
              classNamePrefix="react-select"
              options={suppliers}
              value={suppliers.find((s) => s.value === formData.supplier) || null}
              onChange={handleSupplierChange}
              components={animatedComponents}
              styles={{
                control: (provided) => ({
                  ...provided,
                  borderRadius: "8px",
                  minHeight: "34px",
                }),
              }}
            />
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="alert_days" className="label">Alert Days</label>
              <input
                id="alert_days"
                type="number"
                name="alert_days"
                value={formData.alert_days}
                onChange={handleChange}
                className="input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="status" className="label">
                Status <span style={{ color: "red" }}>*</span>
              </label>
              <Select
                id="status"
                name="status"
                classNamePrefix="react-select"
                options={statusOptions}
                value={statusOptions.find((s) => s.value === formData.status) || null}
                onChange={handleStatusChange}
                components={animatedComponents}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: "8px",
                    minHeight: "34px",
                  }),
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="remarks" className="label">Remarks</label>
            <textarea
              id="remarks"
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              className="textarea"
            />
          </div>

          <div className="form-group-row">
            <div className="button-group-inline">
              <button
                type="button"
                className="button discard"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Discard
              </button>
              <button type="submit" className="button save" disabled={loading}>
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSupplier;